import { useEffect, useMemo, useState } from 'react'

import { EFilterKeys } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import siteStateManager from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { getSessionStorage } from '@src/utils/enhancedSessionStorage'
import { throttle } from '@src/utils/functions/utils'

import { useShouldHideDeptAndCategoryFilter, filterListOnExp } from '../../filterListOnExp'
import { Filter } from '../filterSection.interface'
import { excludeDepartmentFilter } from '../filterUtils'

const FILTER_KEY_GLOBAL = 'global'
const MOBILE_FILTER_DRAWER_STORAGE_KEY = 'mobile-collection-filter-drawer'
const MOBILE_FILTER_COLLAPSE_STATE_KEY = 'mobile-collection-filter-collapse-state'

interface MobileFiltersProps {
  serverFilters: Filter[]
  collectionSlug?: string
}

interface MobileFiltersReturn {
  isDrawerOpen: boolean
  isSortDrawerOpen: boolean
  collapseActiveKeys: string[]
  headerHeight: number
  isScrollingDown: boolean
  filters: Filter[]
  openDrawerWithFilter: (filterName: string) => void
  openSortDrawer: () => void
  closeDrawer: () => void
  closeSortDrawer: () => void
  handleCollapseChange: (activeKeys: string[]) => void
  handleMainFilterClick: () => void
}

const useMobileFilters = ({ serverFilters, collectionSlug }: MobileFiltersProps): MobileFiltersReturn => {
  // Initialize drawer state based on session storage immediately
  const [isDrawerOpen, setIsDrawerOpen] = useState(() => {
    try {
      // Only run on client side
      if (typeof window === 'undefined') return false

      const sessionStorage = getSessionStorage()
      const storedValue = sessionStorage.getItem<string>(MOBILE_FILTER_DRAWER_STORAGE_KEY, { isGlobal: true })
      return storedValue === 'true'
    } catch (error) {
      console.error('Error reading initial filter drawer state:', error)
      return false
    }
  })

  // Initialize collapse state based on session storage
  const [collapseActiveKeys, setCollapseActiveKeys] = useState<string[]>(() => {
    try {
      // Only run on client side
      if (typeof window === 'undefined') return []

      const sessionStorage = getSessionStorage()
      const storedCollapseState = sessionStorage.getItem<string>(MOBILE_FILTER_COLLAPSE_STATE_KEY, { isGlobal: true })
      return storedCollapseState ? JSON.parse(storedCollapseState) : []
    } catch (error) {
      console.error('Error reading initial collapse state:', error)
      return []
    }
  })

  const [isSortDrawerOpen, setIsSortDrawerOpen] = useState(false)
  const [headerHeight, setHeaderHeight] = useState(0)

  // Track if the header is visible (user is scrolling up)
  const isScrollingDown = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsScrollingDown)

  // Get header height when component mounts and when scrolling direction changes
  useEffect(() => {
    if (isBrowser()) {
      const getHeaderHeight = throttle(() => {
        const headerElement = document.getElementById('site-primary-header')
        const subCollectionCardListElement = document.getElementById('subcollection-card-list')
        const subCategoryListElement = document.getElementById('sub-category-list-header')
        const headerHeight = headerElement?.getBoundingClientRect().height
        const subCollectionCardListHeight = subCollectionCardListElement?.getBoundingClientRect().height
        const subCategoryListHeight = subCategoryListElement?.getBoundingClientRect().height

        setHeaderHeight((headerHeight ?? 0) + (subCollectionCardListHeight ?? 0) + (subCategoryListHeight ?? 0))
      }, 50)

      getHeaderHeight()
      window.addEventListener('resize', getHeaderHeight, { passive: true })

      return () => {
        window.removeEventListener('resize', getHeaderHeight)
      }
    }
  }, [isScrollingDown])

  // Effect to persist drawer state
  useEffect(() => {
    if (typeof window === 'undefined') return

    try {
      const sessionStorage = getSessionStorage()
      sessionStorage.setItem(MOBILE_FILTER_DRAWER_STORAGE_KEY, String(isDrawerOpen), { isGlobal: true })
    } catch (error) {
      console.error('Error storing filter drawer state:', error)
    }
  }, [isDrawerOpen])

  // Effect to persist collapse state
  useEffect(() => {
    if (typeof window === 'undefined' || !isDrawerOpen) return

    try {
      const sessionStorage = getSessionStorage()
      sessionStorage.setItem(MOBILE_FILTER_COLLAPSE_STATE_KEY, JSON.stringify(collapseActiveKeys), { isGlobal: true })
    } catch (error) {
      console.error('Error storing collapse state:', error)
    }
  }, [collapseActiveKeys, isDrawerOpen])

  const hideDeptAndCategoryFilter = useShouldHideDeptAndCategoryFilter()
  const filters = useMemo(() => {
    const filteredList = filterListOnExp(
      excludeDepartmentFilter(serverFilters, collectionSlug),
      hideDeptAndCategoryFilter,
    ).filter(item => item.values.length > 1)

    return filteredList
  }, [serverFilters, hideDeptAndCategoryFilter, collectionSlug])

  // Enhanced drawer control with direct filter access
  const openDrawerWithFilter = (filterName: string) => {
    // Then open drawer with that filter expanded
    setIsDrawerOpen(true)
    setCollapseActiveKeys([filterName])
    const filterKey = filterName === EFilterKeys.SUBCOLLECTIONS ? EFilterKeys.PRODUCT_TYPE : filterName
    trackEvent(TELEMETRY_EVENT_NAMES.FILTER_OPENED, {
      filter_key: filterKey,
    })
  }

  // Reset handler for when the main filter button is clicked
  const handleMainFilterClick = () => {
    // Keep all filters collapsed by default
    setCollapseActiveKeys([])
    setIsDrawerOpen(true)
    trackEvent(TELEMETRY_EVENT_NAMES.FILTER_OPENED, {
      filter_key: FILTER_KEY_GLOBAL,
    })
  }

  const closeDrawer = () => {
    setIsDrawerOpen(false)
    setCollapseActiveKeys([])

    // Clear session storage when drawer is explicitly closed
    try {
      const sessionStorage = getSessionStorage()
      sessionStorage.removeItem(MOBILE_FILTER_DRAWER_STORAGE_KEY, { isGlobal: true })
      sessionStorage.removeItem(MOBILE_FILTER_COLLAPSE_STATE_KEY, { isGlobal: true })
    } catch (error) {
      console.error('Error clearing filter drawer state:', error)
    }
  }

  // Add these sort drawer functions
  const openSortDrawer = () => {
    setIsSortDrawerOpen(true)
    trackEvent(TELEMETRY_EVENT_NAMES.SORT_OPENED, {})
  }

  const closeSortDrawer = () => {
    setIsSortDrawerOpen(false)
  }

  // Add a collapseChange handler
  const handleCollapseChange = (activeKeys: string[]) => {
    const newlyOpenedFilters = activeKeys.filter(key => !collapseActiveKeys.includes(key))
    newlyOpenedFilters.forEach(filterKey => {
      const filterName = filterKey === EFilterKeys.SUBCOLLECTIONS ? EFilterKeys.PRODUCT_TYPE : filterKey
      trackEvent(TELEMETRY_EVENT_NAMES.FILTER_OPENED, {
        filter_key: filterName,
      })
    })

    setCollapseActiveKeys(activeKeys)
  }

  return {
    isDrawerOpen,
    isSortDrawerOpen,
    collapseActiveKeys,
    headerHeight,
    isScrollingDown,
    filters,
    handleCollapseChange,
    handleMainFilterClick,
    closeDrawer,
    openDrawerWithFilter,
    openSortDrawer,
    closeSortDrawer,
  }
}

export default useMobileFilters
